import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useScrollStore, useStickyState } from '@marriott/mi-store-utils';
import { GeneralMessage as GeneralMessageComp, GeneralMessageProps } from '@marriott/mi-ui-library';
import { StickyGeneralMessageContainer, StickyGeneralMessagePlaceholder } from './GeneralMessage.styles';

export const GeneralMessageConfig = {
  emptyLabel: 'GeneralMessage',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/generalmessage`,
};

export const GeneralMessage = (props: GeneralMessageProps) => {
  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const navComponentOrder = useScrollStore(state => state.navComponentOrder);
  const componentId = props?.model?.componentId as string | undefined;
  const isRegisteredInStore = useScrollStore(state =>
    componentId ? !!state.registeredComponents[componentId] : false
  );
  const { isSticky, top } = useStickyState(componentId || '');
  const [generalMessageHeight, setGeneralMessageHeight] = useState(0);
  const [fallbackSticky, setFallbackSticky] = useState(false);
  const generalMessageRef = useRef<HTMLDivElement | null>(null);

  const isStickyOnScrollDown = !!props?.model?.scrollSetting?.isStickyOnScrollDown;
  const isStickyOnScrollUp = !!props?.model?.scrollSetting?.isStickyOnScrollUp;
  const shouldUseStoreSticky = !!componentId && navComponentOrder.includes(componentId) && isRegisteredInStore;
  const sticky = shouldUseStoreSticky ? isSticky : fallbackSticky;
  const stickyTop = shouldUseStoreSticky ? top : 0;

  const registerComponent = useCallback(
    (ref: HTMLDivElement) => {
      if (!componentId || !ref || !navComponentOrder.includes(componentId)) {
        return;
      }
      setRegisteredComponent({
        componentId,
        isStickyOnScrollUp,
        isStickyOnScrollDown,
        height: ref.clientHeight,
      });
    },
    [componentId, isStickyOnScrollDown, isStickyOnScrollUp, navComponentOrder, setRegisteredComponent]
  );

  const refCallback = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref) {
        return;
      }
      generalMessageRef.current = ref;
      setGeneralMessageHeight(ref.clientHeight);
      registerComponent(ref);
    },
    [registerComponent]
  );

  useEffect(() => {
    if (!generalMessageRef.current || !componentId) {
      return;
    }
    registerComponent(generalMessageRef.current);
  }, [componentId, navComponentOrder, registerComponent]);

  useEffect(() => {
    if (shouldUseStoreSticky || typeof window === 'undefined') {
      return;
    }
    let previousY = window.scrollY || 0;
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      if (currentY <= 0) {
        setFallbackSticky(false);
      } else if (currentY > previousY) {
        setFallbackSticky(isStickyOnScrollDown);
      } else if (currentY < previousY) {
        setFallbackSticky(isStickyOnScrollUp);
      }
      previousY = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isStickyOnScrollDown, isStickyOnScrollUp, shouldUseStoreSticky]);

  const updatedProps = { ...props, ARIA_EXPAND: 'Expand Message', ARIA_COLLAPSE: 'Collapse Message' };
  const wrapperStyle = useMemo(() => ({ width: sticky ? '100%' : undefined }), [sticky]);

  return (
    <>
      <StickyGeneralMessageContainer
        data-testid="general-message"
        data-component-name="o-common-static-generalmessage"
        $isSticky={sticky}
        $top={stickyTop}
        style={wrapperStyle}
      >
        <EditableComponent config={GeneralMessageConfig} {...props}>
          <GeneralMessageComp {...updatedProps} refCallback={refCallback} />
        </EditableComponent>
      </StickyGeneralMessageContainer>
      {sticky && generalMessageHeight > 0 && <StickyGeneralMessagePlaceholder $height={generalMessageHeight} />}
    </>
  );
};
